'use client';

import React, { useState } from 'react';
import { CheckCircle2, Copy, ArrowRight, Shield, Package } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';

interface GRNSuccessPanelProps {
  grnId: string | null;
  formData: {
    invoiceNumber: string;
    poReference: string;
    supplierName: string;
    challanNumber: string;
  };
  onNewGRN: () => void;
}

export default function GRNSuccessPanel({ grnId, formData, onNewGRN }: GRNSuccessPanelProps) {
  const [copied, setCopied] = React.useState(false);

  const handleCopy = () => {
    if (!grnId) return;
    navigator.clipboard.writeText(grnId).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  if (!grnId) {
    return (
      <div className="bg-card border border-border rounded-xl p-5 flex flex-col items-center justify-center gap-3 min-h-[200px] text-center">
        <div className="w-12 h-12 rounded-full bg-muted/60 flex items-center justify-center">
          <Package size={22} className="text-muted-foreground" />
        </div>
        <div>
          <p className="text-sm font-semibold text-foreground">GRN ID will appear here</p>
          <p className="text-xs text-muted-foreground mt-1">Fill in Invoice No. and PO Reference, then register to generate a GRN.</p>
        </div>
        <div className="mt-1 px-3 py-1.5 rounded-lg bg-muted/40 border border-border">
          <p className="text-xs font-mono text-muted-foreground tracking-widest">GRN-YYYYMMDD-###</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-green-500/30 rounded-xl overflow-hidden">
      {/* Success header */}
      <div className="px-5 py-3 bg-green-500/10 border-b border-green-500/20 flex items-center gap-2">
        <CheckCircle2 size={15} className="text-green-400" />
        <span className="text-sm font-bold text-green-400">GRN Registered</span>
      </div>

      <div className="p-5 space-y-4">
        {/* GRN ID */}
        <div className="flex flex-col gap-1.5">
          <p className="text-2xs font-semibold text-muted-foreground uppercase tracking-wider">Generated GRN ID</p>
          <div className="flex items-center gap-2">
            <span className="flex-1 font-mono text-lg font-bold text-primary tracking-wider">{grnId}</span>
            <button
              onClick={handleCopy}
              className="p-1.5 rounded-lg border border-border hover:bg-muted transition-colors"
              title="Copy GRN ID"
            >
              <Copy size={13} className={copied ? 'text-green-400' : 'text-muted-foreground'} />
            </button>
          </div>
        </div>

        {/* Status */}
        <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-muted/40 border border-border">
          <div className="flex items-center gap-2">
            <Shield size={13} className="text-yellow-400" />
            <span className="text-xs text-muted-foreground">Inventory Status</span>
          </div>
          <StatusBadge status="quarantine" size="sm" />
        </div>

        {/* Details */}
        <div className="space-y-2">
          {formData.invoiceNumber && (
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">Invoice</span>
              <span className="font-medium text-foreground">{formData.invoiceNumber}</span>
            </div>
          )}
          {formData.poReference && (
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">PO Ref</span>
              <span className="font-medium text-foreground">{formData.poReference}</span>
            </div>
          )}
          {formData.challanNumber && (
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">Challan</span>
              <span className="font-medium text-foreground">{formData.challanNumber}</span>
            </div>
          )}
          {formData.supplierName && (
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">Supplier</span>
              <span className="font-medium text-foreground truncate max-w-[140px] text-right">{formData.supplierName}</span>
            </div>
          )}
        </div>

        {/* Next step hint */}
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary/8 border border-primary/20">
          <ArrowRight size={12} className="text-primary flex-shrink-0" />
          <p className="text-2xs text-primary font-medium">Next: Proceed to Verification to check physical quantities</p>
        </div>

        <button
          onClick={onNewGRN}
          className="w-full py-2 rounded-lg border border-border text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-all font-medium"
        >
          + Register Another GRN
        </button>
      </div>
    </div>
  );
}
